<template>
  <header class="vtm-header">
    <button
      class="vtm-header__iconbtn"
      type="button"
      :title="collapse ? $t('header.open') : $t('header.close')"
      :aria-label="collapse ? $t('header.open') : $t('header.close')"
      @click="toggleCollapse"
    >
      <i :class="collapse ? 'el-icon-s-unfold' : 'el-icon-s-fold'"></i>
    </button>

    <div class="vtm-header__title">{{ pageTitle }}</div>

    <div class="vtm-header__spacer"></div>

    <!-- 通知中心（常驻） -->
    <notification-center></notification-center>

    <!-- 主题切换 -->
    <button
      class="vtm-header__iconbtn"
      type="button"
      :title="isDark ? $t('header.themeLight') : $t('header.themeDark')"
      :aria-label="isDark ? $t('header.themeLight') : $t('header.themeDark')"
      @click="toggleTheme"
    >
      <i :class="isDark ? 'el-icon-sunny' : 'el-icon-moon'"></i>
    </button>

    <!-- 全屏 -->
    <button class="vtm-header__iconbtn" type="button" :title="$t('header.screen')" :aria-label="$t('header.screen')" @click="screen">
      <i class="el-icon-full-screen"></i>
    </button>

    <!-- 用户 -->
    <el-dropdown trigger="click" @command="userCommand">
      <button class="vtm-header__user" type="button">
        <el-avatar class="vtm-header__avatar" :size="28" :src="userAvatar">
          {{ (nickName || "?").charAt(0).toUpperCase() }}
        </el-avatar>
        <span class="vtm-header__name">{{ nickName }}</span>
        <i class="el-icon-arrow-down vtm-header__caret"></i>
      </button>
      <el-dropdown-menu slot="dropdown">
        <el-dropdown-item command="password">{{ $t("header.password") }}</el-dropdown-item>
        <el-dropdown-item command="logout" divided>{{ $t("header.loginOut") }}</el-dropdown-item>
      </el-dropdown-menu>
    </el-dropdown>
  </header>
</template>

<script>
  import screenfull from "screenfull";
  import * as echarts from "echarts";
  import { buildMacarons } from "@/utils/macarons";
  import eventBus from "@/utils/eventBus";
  import { THEMES } from "@/utils/theme";
  import notificationCenter from "@/components/NotificationCenter.vue";

  export default {
    name: "sys-header",
    components: {
      notificationCenter
    },
    mounted() {
      eventBus.on("openPasswordDialog", this.handlePassword);
    },
    computed: {
      collapse() {
        return this.$store.state.collapse;
      },
      userAvatar() {
        return this.$store.state.avatar;
      },
      nickName() {
        return this.$store.state.nickName;
      },
      isDark() {
        return this.$store.state.theme === THEMES.DARK;
      },
      pageTitle() {
        return (this.$route.meta && this.$route.meta.title) || this.$route.name || "";
      }
    },
    methods: {
      toggleCollapse() {
        if (document.body.clientWidth > 1200) {
          this.$store.dispatch("collapse", !this.$store.state.collapse);
        } else {
          this.$message.warning(this.$t("header.width"));
        }
      },
      toggleTheme() {
        this.$store.dispatch("setTheme", this.isDark ? THEMES.LIGHT : THEMES.DARK);
        // data-theme 已切换：按新模式重新注册图表主题，并通知各页面重渲染图表（暗色不再白底）
        this.$nextTick(() => {
          echarts.registerTheme("macarons", buildMacarons());
          window.dispatchEvent(new Event("vtm-theme-changed"));
        });
      },
      userCommand(command) {
        if (command === "password") {
          this.handlePassword();
        } else if (command === "logout") {
          this.handleLoginOut();
        }
      },
      handlePassword() {
        this.$emit("password", true);
      },
      handleLoginOut() {
        this.$confirm(this.$t("header.isLoginOut"), this.$t("common.tip"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        }).then(() => {
          if (window.Intercom) {
            window.Intercom("shutdown");
          }
          this.$store.dispatch("loginOut");
          this.$router.push("/login");
        });
      },
      screen() {
        if (screenfull.isEnabled) {
          let that = this;
          screenfull.toggle().then(() => {
            that.$store.commit("INNER_HEIGHT", document.documentElement.clientHeight);
          });
        } else {
          this.$message.warning(this.$t("header.notScreenfull"));
        }
      }
    },
    beforeDestroy() {
      eventBus.off("openPasswordDialog", this.handlePassword);
    }
  };
</script>

<style scoped lang="scss">
  .vtm-header {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 56px;
    flex-shrink: 0;
    padding: 0 16px 0 10px;
    background: var(--vtm-surface);
    border-bottom: 1px solid var(--vtm-line);
  }

  .vtm-header__iconbtn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border: none;
    border-radius: var(--vtm-r-sm);
    background: transparent;
    color: var(--vtm-ink-2);
    font-size: 17px;
    cursor: pointer;
    transition: background-color var(--vtm-speed) ease, color var(--vtm-speed) ease;
  }

  .vtm-header__iconbtn:hover {
    background: var(--vtm-well);
    color: var(--vtm-heading);
  }

  .vtm-header__iconbtn:focus-visible {
    outline: 2px solid var(--vtm-mint-dim);
    outline-offset: 1px;
  }

  .vtm-header__title {
    margin-left: 6px;
    font-family: var(--vtm-display);
    font-size: 15px;
    font-weight: 600;
    color: var(--vtm-heading);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .vtm-header__spacer {
    flex: 1;
  }

  .vtm-header__user {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 8px 4px 4px;
    margin-left: 4px;
    border: none;
    border-radius: 20px;
    background: transparent;
    cursor: pointer;
    transition: background-color var(--vtm-speed) ease;
  }

  .vtm-header__user:hover {
    background: var(--vtm-well);
  }

  .vtm-header__user:focus-visible {
    outline: 2px solid var(--vtm-mint-dim);
    outline-offset: 1px;
  }

  .vtm-header__avatar {
    background: var(--vtm-navy);
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
  }

  [data-theme="dark"] .vtm-header__avatar {
    background: var(--vtm-mint);
    color: var(--vtm-navy);
  }

  .vtm-header__name {
    font-size: 13px;
    font-weight: 600;
    color: var(--vtm-ink);
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .vtm-header__caret {
    font-size: 11px;
    color: var(--vtm-ink-3);
  }

  @media (max-width: 640px) {
    .vtm-header__name {
      display: none;
    }
  }
</style>
